export const NO_INVOICING_VALUE = 'no-invoicing';

/** Query param carrying the uuid of the fiscal data record to open in the form sheet */
export const INVOICE_DATA_UUID_PARAM = 'invoiceDataUuid';

/** Query param selecting which flow the deep-linked fiscal data sheet opens in */
export const INVOICE_DATA_MODE_PARAM = 'invoiceDataMode';

/** Query param carrying the payment failure reason shown by the retry sheet */
export const INVOICE_ERROR_PARAM = 'invoiceError';

/** Query param carrying the uuid of the failed payment the retry sheet re-runs */
export const PAYMENT_UUID_PARAM = 'paymentUuid';

/**
 * Query param narrowing the retry to the CFDIs that failed, instead of re-issuing the whole
 * payment. Set by the retry link of a per-store row, whose siblings hold valid CFDIs that must
 * not be cancelled along the way.
 */
export const ONLY_FAILED_PARAM = 'onlyFailed';

/**
 * Upper bound for the error message carried in the url. Keeps a backend message from
 * pushing the link past what browsers and servers reliably accept, and bounds what a
 * hand-crafted link can render inside the sheet.
 */
export const INVOICE_ERROR_MAX_LENGTH = 200;

/**
 * Steps of the create flow hosted by the fiscal data sheet.
 *
 * `METHOD` is the entry point: the user picks between uploading the SAT proof of fiscal
 * situation (`UPLOAD`) or capturing the fields by hand (`MANUAL`, the original form). The
 * edit and retry modes have a record already, so they always render `MANUAL` directly.
 */
export const InvoiceDataCreationSteps = {
  METHOD: 'method',
  UPLOAD: 'upload',
  MANUAL: 'manual',
} as const;

export type InvoiceDataCreationStep = typeof InvoiceDataCreationSteps[keyof typeof InvoiceDataCreationSteps];

/** Only MIME type accepted for the proof of fiscal situation */
export const FISCAL_PROOF_MIME_TYPE = 'application/pdf';

/** Extension checked alongside the MIME type, since some browsers report an empty type */
export const FISCAL_PROOF_EXTENSION = '.pdf';

/** Stricter than the 10MB the API accepts: a CSF is a couple of pages */
export const FISCAL_PROOF_MAX_SIZE_BYTES = 2 * 1024 * 1024;

/**
 * Deep-link modes accepted by `INVOICE_DATA_MODE_PARAM`.
 *
 * `EDIT` opens the record for a plain save. `RETRY` loads the same fields and saves through
 * the same update request, then re-runs the invoicing of the payment named by
 * `PAYMENT_UUID_PARAM` — so it is the mode the payment history's retry link points at.
 */
export const InvoiceDataDeepLinkModes = {
  EDIT: 'edit',
  RETRY: 'retry',
} as const;

export type InvoiceDataDeepLinkMode = typeof InvoiceDataDeepLinkModes[keyof typeof InvoiceDataDeepLinkModes];
